import { APP_BASE_HREF, CommonModule } from '@angular/common';
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { NguCarousel } from '@ngu/carousel';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { OktaAuthModule, OktaConfig } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { CookieService } from 'ngx-cookie-service';
import { NgxJiraIssueCollectorModule } from 'ngx-jira-issue-collector';
import { environment } from '../environments/environment';
import { AppLayoutComponent } from './_layout/app-layout/app-layout.component';
import {
  CertificateAdapter,
  CourseAdapter,
  ErrorAdapter,
  FilterObjectAdapter,
  MaintenanceAdapter,
  SearchObjectAdapter,
  StaticPageAdapter,
  TestimonialAdapter,
  VideoAdapter,
  VoucherAdapter,
} from './adapters/_index';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ComponentsModule } from './components/components.module';
import { IframeTrackerDirective } from './directives/_index';
import {
  BannerInfoFactory,
  CourseAvailabilityCheckItemFactory,
  FilterCategoryFactory,
  FlexibleLearningItemFactory,
  InstructionsItemFactory,
  LearningPathFactory,
  LearningPlanTilesFactory,
  LearningTypeCheckItemFactory,
  LearningTypeFactory,
  ProductCheckItemFactory,
  RoleCheckItemFactory,
  RoleFactory,
  SubscriptionFactory,
  SubscriptionTilesFactory,
  SubscriptionUIConfigurationItemFactory,
  TopicCheckItemFactory,
  TopicFactory,
} from './factories/_index';
import { IntercepterProviders } from './interceptors/interceptor-provider';
import { MaintenanceDowntimeModule } from './pages/maintenance-downtime/maintenance-downtime.module';
import { NotFoundModule } from './pages/notfound/notfound.module';
import { PipesModule } from './pipes/pipes.module';
import {
  AnnouncementService,
  ApiService,
  CertificationService,
  EmailService,
  FilterCatalogItemService,
  GoogleAnalyticsService,
  LearningPathsService,
  SearchService,
  TermsAndConditionsService,
  TrainingSupportElearningPageService,
  VoucherService,
  WordpressSubscriptionsService,
  WordpressTestimonialsService,
  WordpressVideosService,
} from './services/_index';
import { NotificationService } from './services/wordpress/notification.service';
// import { TooltipModule } from '@cloudfactorydk/ng2-tooltip-directive';
import { UserModule } from './services/user/user.module';

// https://github.com/okta/okta-angular/#oktaauthmoduleforroot
const authConfig = {
  issuer: `${environment.okta.issuer}/oauth2/default`,
  clientId: environment.okta.client_id,
  redirectUri: environment.okta.redirectUri,
  scopes: environment.okta.scopes,
  pkce: true,
};
const oktaAuth = new OktaAuth(authConfig);
// NOTE: Do not use the factory approach.
// onAuthRequired is need for custom login approach.
// onAuthResume leave empty. It will call onAuthRequired.
const moduleConfig: OktaConfig = {
  oktaAuth,
};

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

@NgModule({
  declarations: [AppComponent, AppLayoutComponent, IframeTrackerDirective],
  bootstrap: [AppComponent],
  imports: [
    AppRoutingModule,
    BrowserModule,
    CommonModule,
    ComponentsModule,
    FormsModule,
    UserModule.forRoot(),
    MaintenanceDowntimeModule,
    NguCarousel, // Third-party carousel module used by testimonials and hero banners
    NgxJiraIssueCollectorModule, // Third-party JIRA issue module used by submit a bug
    NotFoundModule,
    OktaAuthModule.forRoot(moduleConfig), // Set configurations above.
    PipesModule,
    ReactiveFormsModule, // Angular Reactive Forms Module
    //TooltipModule, // Third-party tooltip module
    TranslateModule.forRoot({
      defaultLanguage: 'en',
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
  ],
  providers: [
    { provide: APP_BASE_HREF, useValue: '/explore' }, // NOTE: This overrides the base root with the custom explore "/"
    IntercepterProviders,
    AnnouncementService,
    ApiService,
    CertificationService,
    CookieService,
    EmailService,
    ErrorAdapter,
    GoogleAnalyticsService,
    LearningPathsService,
    NotificationService,
    SearchService,
    FilterCatalogItemService,
    TermsAndConditionsService,
    TrainingSupportElearningPageService,
    VoucherService,
    WordpressSubscriptionsService,
    WordpressTestimonialsService,
    WordpressVideosService,
    CertificateAdapter,
    CourseAdapter,
    ErrorAdapter,
    FilterObjectAdapter,
    MaintenanceAdapter,
    SearchObjectAdapter,
    StaticPageAdapter,
    TestimonialAdapter,
    VideoAdapter,
    VoucherAdapter,
    BannerInfoFactory,
    CourseAvailabilityCheckItemFactory,
    FilterCategoryFactory,
    FlexibleLearningItemFactory,
    InstructionsItemFactory,
    LearningPathFactory,
    LearningTypeCheckItemFactory,
    LearningTypeFactory,
    LearningPlanTilesFactory,
    ProductCheckItemFactory,
    RoleCheckItemFactory,
    RoleFactory,
    SubscriptionFactory,
    SubscriptionTilesFactory,
    SubscriptionUIConfigurationItemFactory,
    TopicCheckItemFactory,
    TopicFactory,
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
